import React, { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { EXPLORER_IMAGE, EXPLORER_VIDEO } from "../config/sectionVideos";

const ZONES = [
  { id: "pv", label: "Photovoltaik", x: 38, y: 15, link: "/energie-heizung",
    title: "Photovoltaik auf dem Dach",
    desc: "Eigenstromerzeugung über das Dach. Kombiniert mit Speicher, Wärmepumpe und Energiemanagement zum Eigenverbrauch. Dachausrichtung und Verschattung werden projektabhängig geprüft.",
    tags: ["PV-Module", "Wechselrichter", "Speicher", "Eigenverbrauch"], icon: "☀" },
  { id: "heat", label: "Wärmepumpe", x: 88, y: 72, link: "/energie-heizung",
    title: "Wärmepumpe (außen)",
    desc: "Luft-Wasser-Wärmepumpe als Energiezentrale. Heizt im Winter, kann im Sommer kühlen. Kombinierbar mit Pufferspeicher und PV.",
    tags: ["Heizen", "Warmwasser", "Kühlen", "PV-Kopplung"], icon: "♨" },
  { id: "fbh", label: "Fußbodenheizung", x: 34, y: 69, link: "/energie-heizung",
    title: "Fußbodenheizung",
    desc: "Niedertemperatursystem — ideal zur Wärmepumpe. Zonenregelung je Raum, hoher Komfort.",
    tags: ["Niedertemperatur", "Raumregelung", "Heizen"], icon: "+" },
  { id: "ww", label: "Warmwasser", x: 68, y: 70, link: "/energie-heizung",
    title: "Warmwasser-Speicher",
    desc: "Zentraler Speicher für Warmwasser. Anbindung an Wärmepumpe und ggf. PV-Überschuss. Zirkulation und Zeitprogramme reduzieren Energieverlust.",
    tags: ["Speicher", "Zirkulation", "Anti-Legionellen"], icon: "💧" },
  { id: "light", label: "Licht & Beschattung", x: 35, y: 42, link: "/gebaeudeautomation",
    title: "Licht & Beschattung",
    desc: "Innen- und Außenlicht, Rollläden und Jalousien — mit Szenen, Zeit- und Sensorsteuerung. Verknüpft über die Gebäudeautomation.",
    tags: ["Lichtszenen", "Beschattung", "Zeitprogramme"], icon: "+" },
  { id: "access", label: "Zutritt", x: 47, y: 65, link: "/gebaeudeautomation",
    title: "Zutritt & Türkommunikation",
    desc: "Elektronischer Zutritt mit Berechtigungen, Video-Türkommunikation und App-Anbindung. Löst mechanische Schlüssel ab.",
    tags: ["Zutritt", "Video", "App"], icon: "+" },
  { id: "auto", label: "Gebäudeautomation", x: 55, y: 43, link: "/gebaeudeautomation",
    title: "Gebäudeautomation",
    desc: "Zentrale Steuerung aller technischen Systeme — Heizung, Licht, Beschattung, Zutritt. Bus oder Funk, je nach Projekt.",
    tags: ["Bus", "Funk", "Visualisierung", "App"], icon: "+" },
  { id: "ems", label: "Energiemanagement", x: 66, y: 55, link: "/energie-heizung",
    title: "Energiemanagement",
    desc: "Monitoring aller Verbräuche, Lastmanagement und PV-Überschussnutzung. Steuert Wärmepumpe und Speicher bedarfsgerecht.",
    tags: ["Monitoring", "Lastmanagement", "PV-Überschuss"], icon: "+" },
  { id: "sensor", label: "Sensorik", x: 25, y: 42, link: "/gebaeudeautomation",
    title: "Sensorik",
    desc: "Temperatur, CO₂, Präsenz, Fensterstatus, Verbrauch — die Datenbasis für wirksame Automation.",
    tags: ["Temperatur", "CO₂", "Präsenz", "Verbrauch"], icon: "+" },
];

export default function InteractiveHouse() {
  const [active, setActive] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Lock body scroll when mobile sheet is open
  useEffect(() => {
    if (isMobile && active) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isMobile, active]);

  const showSheet = isMobile && active;

  return (
    <section className="relative site-container section-block overflow-hidden" data-testid="interactive-house">
      {/* Ambient background video */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <video
          src={EXPLORER_VIDEO.src}
          poster={EXPLORER_VIDEO.poster}
          autoPlay muted loop playsInline preload="metadata"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          data-testid="explorer-bg-video"
        />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, #0F2A48 0%, rgba(15,42,72,0.75) 25%, rgba(15,42,72,0.55) 50%, rgba(15,42,72,0.75) 75%, #0F2A48 100%)"
        }}/>
      </div>

      <div className={`relative ${showSheet ? "z-[70]" : "z-10"}`}>
        <div className="mb-12 reveal">
          <div className="eyebrow mb-4 text-[#5FA637]">Haus-Explorer</div>
          <h2 className="section-title max-w-3xl">
            Technik, zum <span className="italic-accent">Erkunden.</span>
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl text-sm md:text-base">
            Tippen Sie auf die Zonen eines Hauses, um zu sehen, welche Technik in diesem Bereich zusammenspielt.
          </p>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Fotorealistischer Haus-Querschnitt mit interaktiven Hotspots */}
        <div className="lg:col-span-7 relative border border-white/10 bg-[#E7EDF0] overflow-hidden aspect-[3/2]" data-testid="interactive-house-visual">
          <img
            src={EXPLORER_IMAGE.src}
            alt={EXPLORER_IMAGE.alt}
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            data-testid="interactive-house-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F38]/20 via-transparent to-white/5 pointer-events-none"/>
          {ZONES.map((z) => {
            const isActive = active?.id === z.id;
            return (
              <button
                key={z.id}
                type="button"
                onClick={() => setActive(z)}
                aria-label={`${z.label} anzeigen`}
                title={z.label}
                data-testid={`hotspot-${z.id}`}
                style={{ left: `${z.x}%`, top: `${z.y}%` }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-white text-[#0A1F38] font-bold shadow-[0_4px_18px_rgba(10,31,56,0.45)] flex items-center justify-center transition-[transform,background-color] duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0A1F38] ${isActive ? "bg-[#7DBE4E] scale-110" : "bg-[#5FA637] animate-pulse"}`}
              >
                <span className="text-sm md:text-base leading-none" aria-hidden="true">{z.icon}</span>
              </button>
            );
          })}
        </div>

        {/* Desktop detail panel */}
        {!isMobile && (
          <div className="lg:col-span-5 border border-white/10 bg-[#163352] p-10 min-h-[400px]">
            {active ? (
              <DetailContent zone={active} onClose={() => setActive(null)}/>
            ) : (
              <PlaceholderContent/>
            )}
          </div>
        )}
      </div>

      {/* Mobile Full-Screen Sheet */}
      {showSheet && (
        <div
          className="lg:hidden fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end"
          onClick={() => setActive(null)}
          data-testid="mobile-sheet-backdrop"
        >
          <div
            className="w-full bg-[#163352] border-t border-[#5FA637]/50 p-6 pb-10 max-h-[85vh] overflow-y-auto animate-sheet-up"
            style={{ animation: "sheet-up 0.3s cubic-bezier(.16,1,.3,1)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-sheet"
          >
            <div className="w-12 h-1 bg-white/30 rounded-full mx-auto mb-6"/>
            <DetailContent zone={active} onClose={() => setActive(null)} isMobile/>
          </div>
          <style>{`
            @keyframes sheet-up {
              from { transform: translateY(100%); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}
      </div>
    </section>
  );
}

function DetailContent({ zone, onClose, isMobile }) {
  return (
    <div data-testid={isMobile ? "hotspot-detail-mobile" : "hotspot-detail"}>
      <div className="flex items-start justify-between mb-4">
        <div className="eyebrow text-[#5FA637]">{zone.label}</div>
        <button onClick={onClose} className="text-white/60 hover:text-white -mr-2 p-2" data-testid={isMobile ? "close-hotspot-mobile" : "close-hotspot"} aria-label="Schließen">
          <X size={isMobile ? 20 : 16}/>
        </button>
      </div>
      <h3 className="font-display text-2xl md:text-3xl mb-4 leading-tight">{zone.title}</h3>
      <p className="text-white/75 leading-relaxed mb-6 text-sm md:text-base">{zone.desc}</p>
      <div className="flex flex-wrap gap-2 mb-8">
        {zone.tags.map((tag, i) => (
          <span key={i} className="text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 bg-white/5 border border-white/15 text-white/70">
            {tag}
          </span>
        ))}
      </div>
      <Link to={zone.link} onClick={onClose} className="btn-primary">
        Mehr erfahren <ArrowRight size={14}/>
      </Link>
    </div>
  );
}

function PlaceholderContent() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center text-white/50">
      <div className="w-12 h-12 rounded-full border-2 border-[#5FA637]/40 flex items-center justify-center mb-5">
        <span className="text-[#5FA637] text-xl">+</span>
      </div>
      <p className="text-sm max-w-xs">Wählen Sie eine Zone, um die technische Erklärung zu sehen.</p>
    </div>
  );
}
